/**
 * Herkunft der Braindump-Gedanken (F-13, F-15, F-20): Journaltag, Wochen-/
 * Monatsreflexion oder Wissensquelle. Rein - fuer Braindump und "Gemerkte
 * Gedanken".
 */
import { monatName } from '@/domain/review/periods';
import { formatDate } from '@/lib/datetime';
import type { GedankenQuelle, GedankenStatus, ID, LocalDate, Thought } from '@/domain/types';

const MUSTER: Record<GedankenQuelle['art'], RegExp> = {
  woche: /^\d{4}-W\d{2}$/,
  monat: /^\d{4}-(0[1-9]|1[0-2])$/,
  // Eine ID: nicht leer, ohne Leerraum, nicht absurd lang.
  wissen: /^\S{1,100}$/,
};

export function quelleGueltig(q: GedankenQuelle): boolean {
  return (q.art === 'woche' || q.art === 'monat' || q.art === 'wissen') && MUSTER[q.art].test(q.key);
}

/** Gehoert der Gedanke zu diesem Journaltag bzw. dieser Reflexion? */
export function gehoertZu(g: Thought, ort: { date: LocalDate } | { quelle: GedankenQuelle }): boolean {
  if ('quelle' in ort) return g.quelle?.art === ort.quelle.art && g.quelle.key === ort.quelle.key;
  return !g.quelle && g.date === ort.date;
}

/** Anzeige und Link zurueck zu der Stelle, an der der Gedanke notiert wurde. */
export function herkunft(g: Thought): { text: string; pfad: string } {
  if (g.quelle?.art === 'woche') {
    const [jahr, kw] = g.quelle.key.split('-W');
    return { text: `Wochenreflexion KW ${Number(kw)}/${jahr}`, pfad: `/rueckblick?woche=${g.quelle.key}` };
  }
  if (g.quelle?.art === 'monat') {
    return { text: `Monatsreflexion ${monatName(g.quelle.key)}`, pfad: `/rueckblick?monat=${g.quelle.key}` };
  }
  if (g.quelle?.art === 'wissen') {
    return { text: `Wissen: ${g.quelle.titel ?? 'Quelle'}`, pfad: `/wissen/${encodeURIComponent(g.quelle.key)}` };
  }
  return { text: formatDate(g.date), pfad: `/journal?datum=${g.date}` };
}

/** Die vier Stufen eines gemerkten Gedankens - ein Baustein fuer Journal, Inbox und „Notizen & Gedanken“. */
export const GEDANKEN_STATUS: { key: GedankenStatus; label: string }[] = [
  { key: 'neu', label: 'Neu' },
  { key: 'spaeter', label: 'Später durchdenken' },
  { key: 'arbeit', label: 'In Arbeit' },
  { key: 'erledigt', label: 'Erledigt / verworfen' },
];

/** Neueste zuerst: nach Datum, dann nach Anlage. */
export const nachDatum = (a: Thought, b: Thought) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt);

/**
 * Gemerkte Gedanken, die in der Inbox stehen (F-48, Entscheidung 1): gemerkt
 * und noch nicht erledigt/verworfen - auch "Spaeter durchdenken" und "In
 * Arbeit" sind noch nicht verarbeitet.
 *
 * Bereichsfilter wie fuer die Aufgaben derselben Inbox (`matchesAreaFilter`):
 * bei aktivem Filter bleibt nur, was zu einem gefilterten Bereich gehoert -
 * ohne Bereich ist ausgeblendet. So gilt in einer Liste und in einer Zahl
 * eine Regel; die Inbox sagt dazu, dass gefiltert ist. `bereich` liefert den
 * Bereich eines Gedankens, z. B. den seines Projekts (F-11).
 */
export function offeneGemerkte(
  gedanken: readonly Thought[],
  aktiveBereiche: readonly ID[] = [],
  bereich: (g: Thought) => ID | null = (g) => g.areaId ?? null,
): Thought[] {
  const passt = (g: Thought) => {
    if (aktiveBereiche.length === 0) return true;
    const b = bereich(g);
    return b != null && aktiveBereiche.includes(b);
  };
  return gedanken
    .filter((g) => g.gemerkt === 1 && g.status !== 'erledigt')
    .filter(passt)
    .sort(nachDatum);
}
